/**
 * Утилита для мониторинга производительности анимации
 * Измеряет FPS и определяет, когда производительность падает ниже порога
 */

interface PerformanceMonitorOptions {
  /** Порог FPS, ниже которого считается низкая производительность */
  threshold: number;
  /** Длительность в миллисекундах, в течение которой FPS должен быть ниже порога */
  duration: number;
  /** Callback вызывается при обнаружении низкой производительности */
  onLowPerformance: (avgFps: number) => void;
  /** Включить детальное логирование FPS в консоль (для отладки) */
  debug?: boolean;
  /** Метим источник логов, чтобы различать несколько мониторов */
  debugLabel?: string;
}

interface MonitorState {
  isActive: boolean;
  lastFrameTime: number;
  fpsHistory: number[];
  maxHistoryLength: number;
  options: PerformanceMonitorOptions;
  animationFrameId: number | null;
  frameCounter: number;
}

class PerformanceMonitor {
  private state: MonitorState | null = null;

  /**
   * Начинает мониторинг производительности
   * @param options Опции мониторинга
   */
  start(options: PerformanceMonitorOptions): void {
    // Останавливаем предыдущий мониторинг, если он активен
    this.stop();

    // Вычисляем максимальную длину истории на основе duration и threshold
    // Храним примерно 3 секунды данных при минимальном FPS (threshold)
    // Добавляем запас для надёжности
    const minFps = Math.max(10, options.threshold);
    const framesPerSecond = minFps;
    const maxHistoryLength = Math.ceil((options.duration / 1000) * framesPerSecond * 1.5);

    this.state = {
      isActive: true,
      lastFrameTime: performance.now(),
      fpsHistory: [],
      maxHistoryLength,
      options,
      animationFrameId: null,
      frameCounter: 0,
    };

    this.measureFrame();
  }

  /**
   * Останавливает мониторинг производительности
   */
  stop(): void {
    if (this.state && this.state.animationFrameId !== null) {
      cancelAnimationFrame(this.state.animationFrameId);
    }
    this.state = null;
  }

  /**
   * Проверяет, активен ли мониторинг
   */
  isActive(): boolean {
    return this.state?.isActive ?? false;
  }

  /**
   * Измеряет FPS текущего кадра и проверяет производительность
   */
  private measureFrame = (): void => {
    if (!this.state || !this.state.isActive) {
      return;
    }

    const now = performance.now();
    const deltaTime = now - this.state.lastFrameTime;

    if (deltaTime > 0) {
      // Вычисляем FPS для текущего кадра
      const fps = 1000 / deltaTime;
      
      // Добавляем в историю
      this.state.fpsHistory.push(fps);
      this.state.frameCounter += 1;
      
      // При включённом debug‑режиме логируем FPS (раз в 10 кадров, чтобы не засорять консоль)
      if (this.state.options.debug && this.state.frameCounter % 10 === 0) {
        const label = this.state.options.debugLabel ?? 'PerformanceMonitor';
        const avgFpsDebug = this.state.fpsHistory.length > 0
          ? this.state.fpsHistory.reduce((sum, value) => sum + value, 0) / this.state.fpsHistory.length
          : fps;
        console.log(
          `[${label}] FPS: ${fps.toFixed(1)}, avg: ${avgFpsDebug.toFixed(1)}, samples: ${this.state.fpsHistory.length}`
        );
      }
      
      // Ограничиваем размер истории
      if (this.state.fpsHistory.length > this.state.maxHistoryLength) {
        this.state.fpsHistory.shift();
      }

      // Проверяем средний FPS, если накопилось достаточно данных
      // Минимум 20 кадров для статистической значимости
      if (this.state.fpsHistory.length >= 20) {
        const avgFps = 
          this.state.fpsHistory.reduce((sum, value) => sum + value, 0) / 
          this.state.fpsHistory.length;

        // Проверяем, ниже ли средний FPS порога
        const state = this.state; // Сохраняем ссылку для TypeScript
        if (!state) return;
        
        if (avgFps < state.options.threshold) {
          // Проверяем, что низкая производительность держится достаточно долго
          // Считаем количество последних кадров с низким FPS
          const recentLowFpsCount = state.fpsHistory.filter(
            (fps) => fps < state.options.threshold
          ).length;
          
          // Если достаточно кадров с низким FPS (примерно duration)
          const requiredFrames = Math.ceil(
            (state.options.duration / 1000) * state.options.threshold
          );
          
          if (recentLowFpsCount >= requiredFrames) {
            // Вызываем callback и останавливаем мониторинг
            state.options.onLowPerformance(avgFps);
            this.stop();
            return;
          }
        }
      }
    }

    if (!this.state) return;
    this.state.lastFrameTime = now;
    this.state.animationFrameId = requestAnimationFrame(this.measureFrame);
  };
}

// Экспортируем singleton экземпляр
let monitorInstance: PerformanceMonitor | null = null;

/**
 * Получить экземпляр монитора производительности
 */
export function getPerformanceMonitor(): PerformanceMonitor {
  if (!monitorInstance) {
    monitorInstance = new PerformanceMonitor();
  }
  return monitorInstance;
}

/**
 * Создать новый экземпляр монитора (для случаев, когда нужны несколько независимых мониторов)
 */
export function createPerformanceMonitor(): PerformanceMonitor {
  return new PerformanceMonitor();
}
